import { useCallback } from 'react';

export interface CellPosition {
  row: number;
  col: number;
}

interface UseTableKeyboardOptions {
  rowCount: number;
  columnCount: number;
  selectedCell: CellPosition | null;
  editingCell: CellPosition | null;
  editMode: boolean;
  selectCell: (cell: CellPosition, options?: { extend?: boolean }) => void;
  enterEdit: (cell: CellPosition) => void;
  exitEdit: () => void;
  onCopy?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return target.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function useTableKeyboard({
  rowCount,
  columnCount,
  selectedCell,
  editingCell,
  editMode,
  selectCell,
  enterEdit,
  exitEdit,
  onCopy,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}: UseTableKeyboardOptions) {
  return useCallback((event: React.KeyboardEvent) => {
    if (event.defaultPrevented) return;
    if (isEditableTarget(event.target)) return;

    const key = event.key;
    const hasSelection = selectedCell !== null;
    const modifier = event.ctrlKey || event.metaKey;

    if (modifier && key.toLowerCase() === 'c') {
      if (onCopy && hasSelection) {
        event.preventDefault();
        onCopy();
      }
      return;
    }

    if (modifier && (key.toLowerCase() === 'z' || key.toLowerCase() === 'y')) {
      if (key.toLowerCase() === 'z' && event.shiftKey) {
        if (onRedo && canRedo) {
          event.preventDefault();
          onRedo();
        }
        return;
      }
      if (key.toLowerCase() === 'y') {
        if (onRedo && canRedo) {
          event.preventDefault();
          onRedo();
        }
        return;
      }
      if (onUndo && canUndo) {
        event.preventDefault();
        onUndo();
      }
      return;
    }

    if (editingCell) {
      if (key === 'Escape') {
        event.preventDefault();
        exitEdit();
      }
      return;
    }

    if (!hasSelection) {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(key)) {
        event.preventDefault();
        if (rowCount > 0 && columnCount > 0) {
          selectCell({ row: 0, col: 0 });
        }
      }
      return;
    }

    if (key === 'Enter') {
      if (editMode && selectedCell) {
        event.preventDefault();
        enterEdit(selectedCell);
      }
      return;
    }

    if (key === 'Escape') {
      event.preventDefault();
      exitEdit();
      return;
    }

    let nextRow = selectedCell.row;
    let nextCol = selectedCell.col;
    let extend = false;

    switch (key) {
      case 'ArrowUp':
        nextRow -= 1;
        extend = event.shiftKey;
        break;
      case 'ArrowDown':
        nextRow += 1;
        extend = event.shiftKey;
        break;
      case 'ArrowLeft':
        nextCol -= 1;
        extend = event.shiftKey;
        break;
      case 'ArrowRight':
        nextCol += 1;
        extend = event.shiftKey;
        break;
      case 'Tab':
        if (event.shiftKey) {
          if (nextCol === 0) {
            nextRow -= 1;
            nextCol = columnCount - 1;
          } else {
            nextCol -= 1;
          }
        } else {
          if (nextCol === columnCount - 1) {
            nextRow += 1;
            nextCol = 0;
          } else {
            nextCol += 1;
          }
        }
        break;
      default:
        return;
    }

    if (rowCount === 0 || columnCount === 0) return;

    nextRow = Math.max(0, Math.min(rowCount - 1, nextRow));
    nextCol = Math.max(0, Math.min(columnCount - 1, nextCol));

    event.preventDefault();
    selectCell({ row: nextRow, col: nextCol }, { extend });
  }, [
    rowCount,
    columnCount,
    selectedCell,
    editingCell,
    editMode,
    selectCell,
    enterEdit,
    exitEdit,
    onCopy,
    onUndo,
    onRedo,
    canUndo,
    canRedo,
  ]);
}
